import { assertImageFileMaxSize } from '../utils/imageUpload';
import { ApiService, API_BASE_URL } from './apiCore';

Object.assign(ApiService.prototype, {
  getFilenameFromDisposition(contentDisposition, fallbackName) {
    if (!contentDisposition) {
      return fallbackName;
    }

    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    if (!match || !match[1]) {
      return fallbackName;
    }

    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  },

  async getTests({ requireAuth = false } = {}) {
    return this.request('/tests', {
      method: 'GET',
      requireAuth,
    });
  },

  async getTest(id) {
    return this.request(`/tests/${id}`, {
      method: 'GET',
    });
  },

  async createTest(testData) {
    return this.request('/tests', {
      method: 'POST',
      body: JSON.stringify(testData),
    });
  },

  async updateTest(id, testData) {
    return this.request(`/tests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(testData),
    });
  },

  async deleteTest(id) {
    return this.request(`/tests/${id}`, {
      method: 'DELETE',
    });
  },

  async deleteTestSoft(id) {
    return this.request(`/tests/${id}`, {
      method: 'DELETE',
    });
  },

  async restoreTest(id) {
    return this.request(`/tests/admin/tests/${id}/restore`, {
      method: 'POST',
    });
  },

  async deleteResult(id) {
    return this.request(`/tests/admin/results/${id}`, {
      method: 'DELETE',
    });
  },

  async clearTestResults(id) {
    return this.request(`/tests/admin/tests/${id}/results`, {
      method: 'DELETE',
    });
  },

  async mergeCategories(payload) {
    return this.request('/tests/admin/categories/merge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async submitTest(id, answers) {
    return this.request(`/tests/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    });
  },

  async getResults() {
    return this.request('/tests/results', {
      method: 'GET',
    });
  },

  async getResult(id) {
    return this.request(`/tests/results/${id}`, {
      method: 'GET',
    });
  },

  async getAllResults() {
    return this.request('/tests/results/all', {
      method: 'GET',
    });
  },

  async exportResults() {
    const url = `${API_BASE_URL}/tests/results/export`;
    const response = await this.fetchWithAuthRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    }, '/tests/results/export');

    if (!response.ok) {
      const data = await this.parseJsonResponse(response);
      throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `test-results-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  async previewTestImport(payload, mode = 'create_new') {
    return this.request('/tests/admin/tests/import/preview', {
      method: 'POST',
      body: JSON.stringify({ payload, mode }),
    });
  },

  async confirmTestImport(payload, mode = 'create_new', targetTestId) {
    const body = { payload, mode };
    if (targetTestId) {
      body.targetTestId = targetTestId;
    }

    return this.request('/tests/admin/tests/import/confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async exportTest(testId) {
    const endpoint = `/tests/admin/tests/${testId}/export`;
    const response = await this.fetchWithAuthRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(),
    }, endpoint);

    if (!response.ok) {
      const data = await this.parseJsonResponse(response);
      throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('content-disposition');
    const fallbackName = `test-${testId}-${new Date().toISOString().split('T')[0]}.json`;
    const fileName = this.getFilenameFromDisposition(contentDisposition, fallbackName);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  async uploadTestImage(file) {
    assertImageFileMaxSize(file);
    const formData = new FormData();
    formData.append('image', file);
    const endpoint = '/images/upload';
    const response = await this.fetchWithAuthRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(true, false),
      body: formData,
    }, endpoint);
    const data = await this.parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  },

  async updateProfile(profileData) {
    return this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  async uploadAvatar(file) {
    assertImageFileMaxSize(file);
    const formData = new FormData();
    formData.append('avatar', file);
    const endpoint = '/users/avatar';
    const response = await this.fetchWithAuthRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(true, false),
      body: formData,
    }, endpoint);
    const data = await this.parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  },

  async changePassword(oldPassword, newPassword) {
    return this.request('/users/password', {
      method: 'PUT',
      body: JSON.stringify({ oldPassword, newPassword }),
    });
  },

  async getUserStats() {
    return this.request('/users/stats', {
      method: 'GET',
    });
  },

  async getUsers() {
    return this.request('/users', {
      method: 'GET',
    });
  },

  async updateUserRole(id, role) {
    return this.request(`/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },

  async updateUserStatus(id, isActive) {
    return this.request(`/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
  },

  async getPsychologistUsers(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set('search', params.search);
    if (params.assignment) searchParams.set('assignment', params.assignment);
    if (params.access) searchParams.set('access', params.access);
    const query = searchParams.toString();
    const endpoint = `/psychologists/users${query ? `?${query}` : ''}`;
    return this.request(endpoint, { method: 'GET' });
  },

  async assignPsychologistUser(userId) {
    return this.request(`/psychologists/users/${userId}/assign`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  async releasePsychologistUser(userId, releaseReason) {
    return this.request(`/psychologists/users/${userId}/release`, {
      method: 'POST',
      body: JSON.stringify({ releaseReason }),
    });
  },

  async requestMaterialsAccess(userId, payload = {}) {
    return this.request(`/psychologists/users/${userId}/access-requests`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async closeMaterialsAccess(requestId, closeReason) {
    return this.request(`/psychologists/access-requests/${requestId}/close`, {
      method: 'POST',
      body: JSON.stringify({ closeReason }),
    });
  },

  async getPsychologistUserResults(userId) {
    return this.request(`/psychologists/users/${userId}/test-results`, {
      method: 'GET',
    });
  },

  async getPsychologistUserTestResult(userId, resultId) {
    return this.request(`/psychologists/users/${userId}/test-results/${resultId}`, {
      method: 'GET',
    });
  },

  async getMyAccessRequests() {
    return this.request('/users/me/access-requests', { method: 'GET' });
  },

  async approveAccessRequest(id, messageFromUser = '') {
    return this.request(`/users/me/access-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ messageFromUser }),
    });
  },

  async rejectAccessRequest(id, messageFromUser = '') {
    return this.request(`/users/me/access-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ messageFromUser }),
    });
  },

  async revokeAccessRequest(id, closeReason) {
    return this.request(`/users/me/access-requests/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ closeReason }),
    });
  },

  async getPublicConfig() {
    return this.request('/config/public', {
      method: 'GET',
      requireAuth: false,
    });
  },

  async getPublicConfigVersion() {
    return this.request('/config/public/version', {
      method: 'GET',
      requireAuth: false,
    });
  },

  async getSubscriptionPlans() {
    return this.request('/subscriptions/plans', {
      method: 'GET',
      requireAuth: false,
    });
  },

  async getMySubscription() {
    return this.request('/subscriptions/me', {
      method: 'GET',
    });
  },

  async createSubscriptionPayment(planId) {
    return this.request('/payments/subscription', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  },

  async getPaymentOrder(orderId) {
    return this.request(`/payments/orders/${orderId}`, {
      method: 'GET',
    });
  },

  async confirmRobokassaReturn(payload) {
    return this.request('/payments/robokassa/confirm', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async forgotPassword(email, captchaToken) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, captchaToken }),
      requireAuth: false,
    });
  },

  async validateResetToken(token) {
    return this.request(`/auth/validate-reset-token?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      requireAuth: false,
    });
  },

  async resetPassword(token, newPassword, captchaToken) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword, captchaToken }),
      requireAuth: false,
    });
  },

  async getRetakeReminders() {
    return this.request('/tests/retake-reminders', { method: 'GET' });
  },

  async getRecommendations() {
    return this.request('/tests/recommendations', { method: 'GET' });
  },

  async getResultRecommendations(resultId) {
    return this.request(`/tests/results/${resultId}/recommendations`, { method: 'GET' });
  },

  async exportResultPdf(resultId) {
    const endpoint = `/tests/results/${resultId}/export/pdf`;
    const response = await this.fetchWithAuthRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(),
    }, endpoint);

    if (!response.ok) {
      const data = await this.parseJsonResponse(response);
      throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('content-disposition');
    const fallbackName = `result-${resultId}.pdf`;
    const fileName = this.getFilenameFromDisposition(contentDisposition, fallbackName);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  async exportPsychologistResultPdf(userId, resultId) {
    const endpoint = `/psychologists/users/${userId}/test-results/${resultId}/export/pdf`;
    const response = await this.fetchWithAuthRetry(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(),
    }, endpoint);

    if (!response.ok) {
      const data = await this.parseJsonResponse(response);
      throw new Error(data?.message || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers.get('content-disposition');
    const fallbackName = `client-result-${resultId}.pdf`;
    const fileName = this.getFilenameFromDisposition(contentDisposition, fallbackName);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  async getPsychologistUserNotes(userId) {
    return this.request(`/psychologists/users/${userId}/notes`, { method: 'GET' });
  },

  async createPsychologistUserNote(userId, text) {
    return this.request(`/psychologists/users/${userId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async deletePsychologistUserNote(userId, noteId) {
    return this.request(`/psychologists/users/${userId}/notes/${noteId}`, {
      method: 'DELETE',
    });
  },

  async addPsychologistResultComment(userId, resultId, text) {
    return this.request(`/psychologists/users/${userId}/test-results/${resultId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async getNotifications() {
    return this.request('/users/me/notifications', { method: 'GET' });
  },

  async getUnreadNotificationsCount() {
    return this.request('/users/me/notifications/unread-count', { method: 'GET' });
  },

  async getAuditLogs(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });
    const query = searchParams.toString();
    return this.request(`/admin/audit-logs${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  async getAdminPaymentOrders(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });
    const query = searchParams.toString();
    return this.request(`/admin/payments/orders${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  async getAdminSubscriptions(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });
    const query = searchParams.toString();
    return this.request(`/admin/payments/subscriptions${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  async getAdminPaymentStats() {
    return this.request('/admin/payments/stats', { method: 'GET' });
  },

  async getAdminSettings() {
    return this.request('/admin/settings', { method: 'GET' });
  },

  async updateAdminSettings(patch) {
    return this.request('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  async reconcilePayments() {
    return this.request('/admin/payments/reconcile', { method: 'POST' });
  },

  async getAdminAnalytics(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });
    const query = searchParams.toString();
    return this.request(`/admin/analytics/overview${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  async getAdminPromoCodes(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });
    const query = searchParams.toString();
    return this.request(`/admin/promo-codes${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  async createAdminPromoCode(payload) {
    return this.request('/admin/promo-codes', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateAdminPromoCode(id, payload) {
    return this.request(`/admin/promo-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async redeemPromoCode(code) {
    return this.request('/subscriptions/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async getPsychologistUserMessages(userId) {
    return this.request(`/psychologists/users/${userId}/messages`, { method: 'GET' });
  },

  async sendPsychologistUserMessage(userId, text) {
    return this.request(`/psychologists/users/${userId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  async getMyMessageThreads() {
    return this.request('/users/me/messages/threads', { method: 'GET' });
  },

  async getMessagingContacts() {
    return this.request('/users/me/messaging/contacts', { method: 'GET' });
  },

  async getMyMessages(psychologistId) {
    return this.request(`/users/me/messages/${psychologistId}`, { method: 'GET' });
  },

  async sendMyMessage(psychologistId, text) {
    return this.request('/users/me/messages', {
      method: 'POST',
      body: JSON.stringify({ psychologistId, text }),
    });
  },
});
